import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AdminUserService, AdminUserRow } from '../../services/admin-user';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

/**
 * Admin Users — mint standalone ADMIN logins (no partner behind them), reset any
 * login's password, enable/disable accounts. Partner-created logins are listed too
 * (read-only status/password actions) so this is the one place to see every login.
 */
@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, DialogModule, TableModule,
    ButtonModule, TagModule, TooltipModule, InputTextModule, IconFieldModule,
    InputIconModule, IndDatePipe,
  ],
  templateUrl: './admin-users.html',
  styleUrls: ['./admin-users.css'],
})
export class AdminUsersPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(AdminUserService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  private pageTitleSvc = inject(PageTitleService);

  readonly title = computed(() => this.pageTitleSvc.title() || 'Admin Users');

  loading = signal(false);
  users = signal<AdminUserRow[]>([]);
  search = signal('');

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.users();
    return this.users().filter(
      (u) =>
        (u.username || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.mobileNo || '').includes(q) ||
        (u.roles || '').toLowerCase().includes(q) ||
        (u.partnerName || '').toLowerCase().includes(q),
    );
  });

  /** The logged-in account (cannot disable itself; highlighted in the list). */
  readonly myUserId = Number(this.auth.getUser()?.user_id) || 0;

  // ===================== create dialog =====================
  showCreate = signal(false);
  saving = signal(false);
  showPassword = signal(false);

  // ---- mobile OTP verification state ----
  otpSending = signal(false);
  /** The mobile number an OTP was sent to ('' = none yet). Editing the number resets it. */
  otpSentTo = signal('');
  otpExpiryMin = signal(5);

  form: FormGroup = this.fb.group(
    {
      username: ['', [Validators.required, Validators.minLength(3), this.notTaken('username')]],
      email: ['', [Validators.required, Validators.email, this.notTaken('email')]],
      mobileNo: ['', [Validators.required, Validators.pattern(/^\d{10}$/), this.notTaken('mobileNo')]],
      otp: ['', [Validators.required, Validators.pattern(/^\d{4,8}$/)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: [this.passwordsMatch] },
  );

  /**
   * Instant duplicate check against the loaded login list — the server re-checks with the
   * full table regardless, this only surfaces the clash while typing instead of on submit.
   */
  private notTaken(kind: 'username' | 'email' | 'mobileNo') {
    return (c: AbstractControl): ValidationErrors | null => {
      const v = String(c.value ?? '').trim().toLowerCase();
      if (!v) return null;
      const hit = this.users().some((u) => {
        if (kind === 'username') return (u.username || '').toLowerCase() === v;
        if (kind === 'email') return (u.email || '').toLowerCase() === v;
        return (u.mobileNo || '').replace(/\D/g, '') === v.replace(/\D/g, '');
      });
      return hit ? { taken: true } : null;
    };
  }

  private passwordsMatch(g: AbstractControl): ValidationErrors | null {
    const p = g.get('password')?.value;
    const c = g.get('confirmPassword')?.value;
    return p && c && p !== c ? { passwordMismatch: true } : null;
  }

  // ===================== reset-password dialog =====================
  showReset = signal(false);
  resetting = signal(false);
  resetTarget = signal<AdminUserRow | null>(null);
  showResetPassword = signal(false);

  resetForm: FormGroup = this.fb.group(
    {
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: [this.resetPasswordsMatch] },
  );

  private resetPasswordsMatch(g: AbstractControl): ValidationErrors | null {
    const p = g.get('newPassword')?.value;
    const c = g.get('confirmPassword')?.value;
    return p && c && p !== c ? { passwordMismatch: true } : null;
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    // Editing the mobile number invalidates any OTP already sent — the code belongs
    // to the OLD number, so the user must request a fresh one.
    this.form.get('mobileNo')?.valueChanges.subscribe((v: string) => {
      if (this.otpSentTo() && v !== this.otpSentTo()) {
        this.otpSentTo.set('');
        this.form.get('otp')?.reset('');
      }
    });
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.users.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  isAdminRole(u: AdminUserRow): boolean {
    return (u.roles || '').toLowerCase().includes('admin');
  }

  // ===================== create =====================
  openCreate() {
    this.showPassword.set(false);
    this.otpSentTo.set('');
    this.form.reset({ username: '', email: '', mobileNo: '', otp: '', password: '', confirmPassword: '' });
    this.showCreate.set(true);
  }

  closeCreate() {
    this.showCreate.set(false);
  }

  /** Send the verification OTP to the (new) admin's mobile number. */
  sendOtp() {
    const ctrl = this.form.get('mobileNo');
    const mobile = (ctrl?.value || '').trim();
    if (ctrl?.errors?.['taken']) {
      ctrl.markAsTouched();
      this.toast.warning('This mobile number is already used by another login');
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      ctrl?.markAsTouched();
      this.toast.warning('Enter a valid 10-digit mobile number first');
      return;
    }
    this.otpSending.set(true);
    this.svc.sendOtp(mobile).subscribe({
      next: (res: any) => {
        this.otpSending.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to send the OTP');
          return;
        }
        this.otpSentTo.set(mobile);
        this.otpExpiryMin.set(Number(res?.data?.expiresInMinutes) || 5);
        this.form.get('otp')?.reset('');
        // The API distinguishes "queued" from "actually deliverable": with no SMS/WhatsApp
        // provider configured the code cannot reach the phone, so warn instead of claiming
        // success — otherwise the admin waits for a message that will never arrive.
        if (res?.data?.delivered === false) {
          this.toast.popup(
            res?.message || 'No SMS or WhatsApp provider is configured yet, so the OTP cannot reach the phone.',
            'OTP could not be delivered',
            'warning',
          );
        } else {
          this.toast.success(res?.message || 'OTP sent to the mobile number');
        }
      },
      error: () => this.otpSending.set(false),
    });
  }

  save() {
    if (!this.otpSentTo()) {
      this.toast.warning('Verify the mobile number first — tap "Send OTP"');
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.errors?.['passwordMismatch']) this.toast.warning('Passwords do not match');
      else this.toast.warning('Please complete all fields correctly');
      return;
    }
    const v = this.form.getRawValue();
    this.saving.set(true);
    this.svc.create({
      username: v.username.trim(),
      email: v.email.trim(),
      mobileNo: v.mobileNo.trim(),
      password: v.password,
      otp: (v.otp || '').trim(),
    }).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to create the login');
          return;
        }
        this.toast.success(res?.message || 'Admin login created');
        this.closeCreate();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== reset password =====================
  openReset(u: AdminUserRow) {
    this.resetTarget.set(u);
    this.showResetPassword.set(false);
    this.resetForm.reset({ newPassword: '', confirmPassword: '' });
    this.showReset.set(true);
  }

  closeReset() {
    this.showReset.set(false);
    this.resetTarget.set(null);
  }

  saveReset() {
    const target = this.resetTarget();
    if (!target) return;
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      if (this.resetForm.errors?.['passwordMismatch']) this.toast.warning('Passwords do not match');
      else this.toast.warning('Password must be at least 8 characters');
      return;
    }
    this.resetting.set(true);
    this.svc.resetPassword(target.userId, this.resetForm.get('newPassword')?.value).subscribe({
      next: (res: any) => {
        this.resetting.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to reset the password');
          return;
        }
        this.toast.success(res?.message || 'Password reset');
        this.closeReset();
      },
      error: () => this.resetting.set(false),
    });
  }

  // ===================== enable / disable =====================
  async toggleStatus(u: AdminUserRow) {
    if (u.status) {
      const ok = await this.toast.confirm(
        `"${u.username}" will no longer be able to log in, and their active sessions end.`,
        'Disable this login?',
        'Disable',
      );
      if (!ok) return;
    }
    const req = u.status ? this.svc.disable(u.userId) : this.svc.enable(u.userId);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to update the login');
          return;
        }
        this.toast.success(res?.message || 'Updated');
        this.load();
      },
    });
  }
}
